import { createGlobalState } from '@vueuse/core';
import type { RouteNamedMap } from 'vue-router/auto-routes';
import eventBus from '~/event_bus';
import { fetchCurrentUser } from '~/utils/auth';
import type { UserModule } from '~/types';
import { isRouteAllowed } from '~/utils/route_guard';
import { useSize } from '~/composable/use_size';
import { NOTE_OVERVIEW_ENABLED_ON_MOBILE } from '~/constants';

// Detached global scope, same reasoning as the shared authenticated instance in route_guard.ts
const useSharedSize = createGlobalState(() => useSize());

// Always allowed through, regardless of auth state — this is where auth gets established
export const ALWAYS_ALLOWED_ROUTES: (keyof RouteNamedMap)[] = [
    '/auth/callback',
];

/**
 * Whether the current session is valid.
 */
async function isLoggedIn(): Promise<boolean> {
    try {
        await fetchCurrentUser();
        return true;
    } catch {
        return false;
    }
}

export const install: UserModule = ({ app, router }) => {
    router.beforeResolve(async (to, _from) => {
        if (typeof to.name !== 'string' || ALWAYS_ALLOWED_ROUTES.includes(to.name)) {
            return;
        }

        const route_name = to.name;

        if (!await isLoggedIn()) {
            eventBus.emit('close.all');

            return false;
        }

        // isRouteAllowed lazily initializes a shared useAuthenticated() instance, which
        // needs Vue's injection context (for useQueryClient) that a navigation guard doesn't have
        const allowed = app.runWithContext(() => isRouteAllowed(route_name));

        if (!allowed) {
            return { path: '/dashboard' };
        }

        // Notes overview is desktop-only unless explicitly enabled for mobile
        if (route_name === '/overview/notes' && !NOTE_OVERVIEW_ENABLED_ON_MOBILE && !app.runWithContext(() => useSharedSize().bigger.lg)) {
            return { path: '/overview' };
        }
    });
};
